import React, { Component } from 'react';
import { clampPaneWidth, MIN_PANE, STEP } from './paneWidths';

/**
 * PaneResizer — the grab strip between two panes.
 *
 * It owns the gesture and nothing else: no width, no preference, no layout. Its caller holds the
 * live width in state and persists it on `onCommit`, which fires once at the end of a gesture —
 * `Preferences.set` notifies every subscriber in the app, and doing that per pointermove is a
 * re-render a frame.
 *
 * Props:
 *   side     — 'right' | 'left': which edge of its own column it sits on, and therefore which way a
 *              rightward drag reads. A dock to the RIGHT of its handle widens as the handle goes left.
 *   label    — the accessible name
 *   width    — the pane's stored width, or null while the stylesheet decides it
 *   measure  — () => number: the pane's rendered width, where a gesture starting from the
 *              stylesheet's value has nothing stored to start from
 *   bounds   — () => ({ min, max }), measured when the gesture starts rather than held in state,
 *              because the row it is measured against changes size without this component rendering
 *   onResize — (width) => void, every frame of the gesture
 *   onCommit — (width) => void, once at its end
 */
class PaneResizer extends Component {
  constructor(props) {
    super(props);
    this._drag = null;
    this._keyed = null;
  }

  componentWillUnmount() {
    this._stopListening();
  }

  _sign() {
    return 'left' === this.props.side ? -1 : 1;
  }

  // A stored width wider than the room there is now (a pane sized on a wide monitor, opened on a narrow one) is
  // not what is on screen: flex has already given the room back. Starting from it leaves a dead zone, with
  // the pointer travelling the whole overshoot before the pane moves.
  _base(max) {
    return Math.min(this.props.width || this.props.measure(), Number.isFinite(max) ? max : Infinity);
  }

  // The clamp reads a non-positive width as 'nothing stored' (null), so lift to the minimum first.
  _fit(raw, { min, max }) {
    const floor = Number.isFinite(min) ? min : MIN_PANE;
    return clampPaneWidth(Math.max(floor, raw), { min, max });
  }

  _stopListening() {
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
  }

  _onPointerDown = (e) => {
    if (e.button) return;                       // the primary button only; 0 is primary
    e.preventDefault();                         // a drag over text selects it otherwise
    const { min, max } = this.props.bounds();
    // preventDefault also stops the browser focusing the strip; without this the arrow keys do nothing after a
    // drag. After the measurement, and without scrolling, so focusing can never move the row being measured.
    e.currentTarget.focus({ preventScroll: true });
    const start = this._base(max);
    this._drag = { x: e.clientX, start, min, max, latest: null };
    // Capture keeps the move events coming while the pointer leaves the 9px strip. jsdom has no
    // implementation, which is why this is guarded rather than assumed.
    if (e.target.setPointerCapture && undefined !== e.pointerId) {
      e.target.setPointerCapture(e.pointerId);
    }
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
  };

  _onPointerMove = (e) => {
    if (!this._drag) return;
    const { x, start, min, max } = this._drag;
    const next = this._fit(start + (e.clientX - x) * this._sign(), { min, max });
    if (null === next) return;
    this._drag.latest = next;
    this.props.onResize(next);
  };

  _onPointerUp = () => {
    if (!this._drag) return;
    const { latest } = this._drag;
    this._drag = null;
    this._stopListening();
    if (null !== latest) this.props.onCommit(latest);
  };

  _onKeyDown = (e) => {
    const direction = 'ArrowLeft' === e.key ? -1 : 'ArrowRight' === e.key ? 1 : 0;
    if (!direction) return;
    e.preventDefault();
    const bounds = this.props.bounds();
    const next = this._fit(this._base(bounds.max) + direction * STEP * this._sign(), bounds);
    if (null === next) return;
    this._keyed = next;
    this.props.onResize(next);
  };

  _onKeyUp = (e) => {
    if ('ArrowLeft' !== e.key && 'ArrowRight' !== e.key) return;
    if (null === this._keyed) return;
    const width = this._keyed;
    this._keyed = null;
    this.props.onCommit(width);
  };

  render() {
    const { side, label } = this.props;
    return (
      <div
        className={`mc-pane-resizer mc-pane-resizer-${side}`}
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        tabIndex={0}
        onPointerDown={this._onPointerDown}
        onKeyDown={this._onKeyDown}
        onKeyUp={this._onKeyUp}
      />
    );
  }
}

export default PaneResizer;
